import type { TextItem } from "./pdfPageContent";

interface BoxLike { x0: number; y0: number; x1: number; y1: number }
interface WordLike { text: string; bbox?: BoxLike; confidence?: number; baseline?: { has_baseline?: boolean; y0?: number; y1?: number } }
interface LineLike { text?: string; bbox?: BoxLike; words?: WordLike[] }
export interface OcrWordsPage {
  blocks?: { paragraphs?: { lines?: LineLike[] }[] }[] | null;
}

const NO_DESCENDER = /^[^gjpqyQ,;()[\]{}|/\\$]+$/;

const median = (v: number[]) => {
  const s = [...v].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? 0;
};

/**
 * A narrow strip of 1-2 character "words" (clause numbers down a left margin read as one vertical line:
 * "© 0 NO UB Ww Ne") is noise, not text (PW-12). Real lines contain at least one word of three or more letters.
 */
function isMarginNoise(words: WordLike[], box: BoxLike | undefined, pageRight: number): boolean {
  if (words.length < 3 || !box || pageRight <= 0) return false;
  if (box.x1 - box.x0 > 0.15 * pageRight) return false;
  return !words.some((w) => (w.text.match(/[A-Za-z\u00C0-\u024F\u0370-\u04FF]/g) ?? []).length >= 3);
}

/**
 * OCR words as positioned text, in the same shape pdf.js gives for a page with a text layer, so the
 * table finder works on scans too. Tesseract boxes include descenders, so a word's baseline is taken
 * from the words on its line that have none (otherwise "Vendor" and "Property" would sit on different rows).
 * `sx`/`sy` convert picture pixels to page points.
 */
export function ocrWordsToItems(page: OcrWordsPage, sx: number, sy: number): TextItem[] {
  const items: TextItem[] = [];
  let pageRight = 0;
  for (const block of page.blocks ?? []) for (const para of block.paragraphs ?? []) for (const line of para.lines ?? []) pageRight = Math.max(pageRight, line.bbox?.x1 ?? 0);
  for (const block of page.blocks ?? []) {
    for (const para of block.paragraphs ?? []) {
      for (const line of para.lines ?? []) {
        const words = (line.words ?? []).filter((w) => w.text && w.text.trim() && w.bbox);
        if (!words.length) continue;
        if (isMarginNoise(words, line.bbox, pageRight)) continue;
        const heights = words.map((w) => w.bbox!.y1 - w.bbox!.y0);
        const lineH = median(heights);
        const clean = words.filter((w) => NO_DESCENDER.test(w.text));
        const baselinePx = clean.length ? median(clean.map((w) => w.bbox!.y1)) : median(words.map((w) => w.bbox!.y1)) - 0.2 * lineH;
        // Font size from letter heights: capitals and digits are about 0.72 em tall; a box with
        // descenders is about 0.95 em. (Too small a size makes the table finder think rows are far apart.)
        const cleanH = clean.map((w) => w.bbox!.y1 - w.bbox!.y0).sort((a, b) => a - b);
        const tall = cleanH.length ? cleanH[Math.floor(cleanH.length * 0.75)] : 0;
        const sizePt = Math.max(6, tall ? (tall * sy) / 0.72 : (lineH * sy) / 0.95);
        for (const w of words) {
          const b = w.bbox!;
          items.push({
            str: w.text.trim(),
            x: b.x0 * sx,
            baseline: baselinePx * sy,
            width: Math.max(1, (b.x1 - b.x0) * sx),
            size: sizePt,
            bold: false,
            italic: false,
          });
        }
      }
    }
  }
  return items;
}
